// Client-side avatar compression pipeline.
//
// Takes a user-picked File (from an <input type="file">), draws it to a
// canvas at 256×256 (center-cropped so portraits and landscapes both
// end up as clean squares), and re-encodes as WebP at q60. Typical
// output: 5–15KB per avatar.
//
// Returns a data URL string ready for `PUT /members/:id/avatar` — the
// server route parses it, strips the base64 payload, and stores the
// bytes in core.UserAvatars.
//
// WebP support: all modern browsers (Chrome 32+, Firefox 65+, Safari
// 14+, Edge 79+). Older Safari (< 14) falls back to JPEG q75 which is
// larger but still fine for club-sized member counts.

const OUTPUT_SIZE = 256;   // final square edge in px
const WEBP_QUALITY = 0.60;
const JPEG_QUALITY = 0.75;

async function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image.")); };
    img.src = url;
  });
}

function centerCropToSquare(ctx, img, size) {
  const src = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = (img.naturalWidth  - src) / 2;
  const sy = (img.naturalHeight - src) / 2;
  ctx.drawImage(img, sx, sy, src, src, 0, 0, size, size);
}

function canvasToDataUrl(canvas) {
  // Prefer WebP for the smallest bytes. If the browser refuses the
  // format (returns "data:," for unsupported), fall back to JPEG.
  const webp = canvas.toDataURL("image/webp", WEBP_QUALITY);
  if (webp && webp.startsWith("data:image/webp")) return webp;
  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}

export async function compressAvatar(file) {
  if (!file || !file.type || !file.type.startsWith("image/")) {
    throw new Error("Please pick an image file.");
  }
  // Guardrail — refuse anything bigger than 20MB before we even try to
  // decode it, so a 100MB DSLR upload doesn't OOM the browser.
  if (file.size > 20 * 1024 * 1024) {
    throw new Error("Image is too large (max 20MB before compression).");
  }
  const img = await fileToImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext("2d");
  // High-quality downscale
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  centerCropToSquare(ctx, img, OUTPUT_SIZE);
  return canvasToDataUrl(canvas);
}

// Build the <img src> URL for a member. Uses `avatarUpdatedAt` from the
// listMembers response as a cache-buster query param so a fresh upload
// invalidates any browser cache instantly.
export function avatarSrc(member, apiBase) {
  if (!member?.avatarUpdatedAt) return null;
  const base = apiBase || (import.meta.env.VITE_API_BASE_URL || "http://localhost:5000/api/v1");
  const ts = new Date(member.avatarUpdatedAt).getTime();
  return `${base}/members/${member.id}/avatar?v=${ts}`;
}
